import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '../../components/icons';
import { Alert, Card, ChannelGlyph, StatusBadge, buttonClasses } from '../../components/ui';
import { postsApi, type ScheduledPost } from '../../lib/api';
import { formatCountdown, formatScheduled, formatWhen } from '../../lib/format';
import { useSchedulingZone } from '../../lib/timezone';
import { useFeatures } from '../billing/plan';
import { PLATFORM_NAMES, platformOf } from '../posts/limits';
import { MediaThumb } from '../posts/MediaPanel';
import { PostThumb, TYPE_ICONS, captionOf, postTitle, typeLabel, useStoryLabel } from '../posts/postRows';
import { KINDS, kindOf, nextScheduled, openTarget, untimedByCampaign } from '../posts/schedule';
import { errorMessage } from './widgets';

/**
 * "Prochaine mise en ligne" (Murmura M1, `P1-Dashboard.dc.html`, D-186): the
 * next scheduled post or story on a dark hero (its media, format and account,
 * the countdown, the date in the workspace's zone, the caption, its channels),
 * then "Ensuite" with the next three. Read from `['posts']`, the list
 * Publications uses, without polling: only that page polls (PLAN.md §7).
 * Not shown when the plan turns scheduled posts off: Publications carries
 * the upsell (D-183). Since D-189 it sits below the automations and is not
 * shown either while nothing is scheduled.
 */

/** How many items "Ensuite" lists under the hero. */
const THEN_MAX = 3;

const channelOf = (post: Pick<ScheduledPost, 'channelAccount'>) => (platformOf(post.channelAccount.channelCode) === 'facebook' ? 'facebook' : 'instagram');

const targetOf = (posts: readonly ScheduledPost[], post: ScheduledPost) => openTarget(posts, post, KINDS[kindOf(post)].editTo);

/** The hero's picture: a Reel's cover, else the first photo or video, else the format's icon on the brand colour. */
function HeroMedia({ post }: { post: ScheduledPost }) {
  const media = (post.type === 'reel' ? post.cover : null) ?? post.items[0]?.asset ?? null;
  return (
    <span className="relative flex aspect-[4/5] w-[168px] shrink-0 items-center justify-center overflow-hidden rounded-(--radius-control) bg-primary-600 text-white">
      {media ? <MediaThumb asset={media} className="absolute inset-0 size-full" /> : <Icon name={TYPE_ICONS[post.type]} className="size-8" />}
    </span>
  );
}

/** The channels it goes out on: one glyph, or two stacked when a Page copy goes too (D-143). */
function Channels({ post }: { post: ScheduledPost }) {
  const { t } = useTranslation();
  const platform = PLATFORM_NAMES[platformOf(post.channelAccount.channelCode)];
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex shrink-0">
        <ChannelGlyph channel={channelOf(post)} className="ring-2 ring-nav-bg" />
        {post.crossPost ? <ChannelGlyph channel={channelOf(post.crossPost)} className="-ms-1.5 ring-2 ring-nav-bg" /> : null}
      </span>
      <span className="truncate text-ds-sm text-(--color-nav-text)">
        {post.crossPost ? t('dashboard.next.crossPosted', { platform, page: post.crossPost.channelAccount.displayName }) : platform}
      </span>
    </span>
  );
}

function Hero({ post, zone, to }: { post: ScheduledPost; zone: string; to: string | null }) {
  const { t } = useTranslation();
  const storyLabel = useStoryLabel();
  const countdown = formatCountdown(post.scheduledAt!);
  const caption = post.type === 'story' ? postTitle(post, storyLabel(post, zone)).text : captionOf(post);
  const body = (
    <>
      <HeroMedia post={post} />
      <span className="flex min-w-0 flex-[1_1_260px] flex-col gap-2">
        <span className="flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 text-ds-sm font-semibold">{t('dashboard.next.meta', { format: typeLabel(post.type), account: post.channelAccount.displayName })}</span>
          <StatusBadge status="scheduled" className="!bg-surface" />
        </span>
        {countdown ? (
          <span className="mt-1.5 flex flex-col">
            <span className="text-ds-sm text-(--color-nav-text)">{t('dashboard.next.leavesIn')}</span>
            <span className="tabular text-[34px] font-bold leading-[38px] tracking-[-0.02em]">{countdown.span}</span>
          </span>
        ) : null}
        <span className="flex items-center gap-1.5 text-ds-sm text-(--color-nav-text)">
          <Icon name="clock" className="size-[14px] shrink-0" />
          {formatWhen(post.scheduledAt!, { scheduled: true, timeZone: zone })}
        </span>
        {caption ? <span className="mt-1 line-clamp-3 break-words text-ds-base">{caption}</span> : null}
        <span className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
          <Channels post={post} />
          {to ? (
            <span className="inline-flex h-[30px] items-center rounded-ds-sm px-2.5 text-ds-sm font-semibold ring-1 ring-inset ring-nav-muted transition-colors group-hover:bg-nav-hover">
              {t('dashboard.next.open')}
            </span>
          ) : null}
        </span>
      </span>
    </>
  );
  const frame = 'flex flex-wrap items-stretch gap-[18px] rounded-(--radius-node) bg-nav-bg p-4 text-white';
  return to ? (
    <Link to={to} className={`group ${frame}`}>
      {body}
    </Link>
  ) : (
    <div className={frame}>{body}</div>
  );
}

function ThenRow({ post, zone, to }: { post: ScheduledPost; zone: string; to: string | null }) {
  const { t } = useTranslation();
  const storyLabel = useStoryLabel();
  const parts = formatScheduled(post.scheduledAt!, { timeZone: zone });
  const title = postTitle(post, storyLabel(post, zone));
  const content = (
    <>
      <PostThumb post={post} size="sm" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={`truncate text-ds-base font-semibold ${title.isPlaceholder ? 'text-ink-3' : 'text-ink'}`}>{title.text}</span>
        <span className="truncate text-ds-sm text-ink-3">
          {parts ? t('dashboard.next.thenMeta', { date: parts.date, time: parts.time, account: post.channelAccount.displayName }) : post.channelAccount.displayName}
        </span>
      </span>
      <StatusBadge status="scheduled" />
    </>
  );
  const row = 'flex min-w-0 items-center gap-3 rounded-ds-lg border border-border px-3 py-2.5';
  return to ? (
    <Link to={to} className={`${row} transition-colors hover:bg-surface-2/60`}>
      {content}
    </Link>
  ) : (
    <div className={row}>{content}</div>
  );
}

export function NextUp({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const zone = useSchedulingZone();
  const locked = useFeatures().isLocked('scheduledPosts');
  // The key Publications uses, so both share one cache; no refetchInterval here (PLAN.md §7).
  const posts = useQuery({ queryKey: ['posts'], queryFn: postsApi.list });
  if (locked) return null;

  const all = posts.data ?? [];
  const next = nextScheduled(all);
  const [hero, ...rest] = next;
  const then = rest.slice(0, THEN_MAX);
  const untimed = untimedByCampaign(all);

  // Only when something goes live (D-189): a workspace that does not publish gets no card. Below the
  // automations, it waits for its data and the workspace's zone (D-146) rather than holding a skeleton.
  if (!posts.isError && (posts.isLoading || !zone || !hero)) return null;

  let content;
  if (posts.isError || !zone || !hero) {
    content = <Alert>{errorMessage(posts.error, t)}</Alert>;
  } else {
    // Everything scheduled is on the card: say so, and name a lone campaign still waiting for times.
    const allShown = next.length <= 1 + THEN_MAX;
    content = (
      <div className="flex flex-col gap-3.5">
        <Hero post={hero} zone={zone} to={targetOf(all, hero)} />
        {then.length > 0 || allShown ? (
          <div className="flex flex-col gap-2">
            {then.length > 0 ? (
              <>
                <h3 className="text-ds-xs font-semibold uppercase tracking-[0.04em] text-ink-3">{t('dashboard.next.then')}</h3>
                <ul className="flex flex-col gap-2">
                  {then.map((post) => (
                    <li key={post.id}>
                      <ThenRow post={post} zone={zone} to={targetOf(all, post)} />
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {allShown ? (
              <p className="text-ds-sm text-ink-3">
                {t('dashboard.next.nothingElse')}
                {untimed.length === 1 ? ` ${t('dashboard.next.untimed', { count: untimed[0]!.count, name: untimed[0]!.name })}` : null}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Card
      title={t('dashboard.next.title')}
      className={className}
      // "Calendrier" (`P1-Dashboard.dc.html` l.44, D-188 Links.), in every state of the card.
      aside={
        <Link to="/posts?view=calendar" className={buttonClasses('secondary', 'sm')}>
          <Icon name="calendar" className="size-3.5" />
          {t('calendar.views.calendar')}
        </Link>
      }
    >
      {content}
    </Card>
  );
}
